import {Biometric, BiometricWrapper} from "../models/Biometric";
import {genericError} from "../models/GenericError";
import {ListQuery} from "../models/ListQuery";
import {APIConnector} from "./api/APIConnector";
import {createPagination} from "./utilities/paginationBuilder";
import {StoreEntityResult} from '../models/services/StoreEntityResult';
import {createPartialURL, getFilterClause} from "./utilities/urlBuilder";
import {Deserializer} from "jsonapi-serializer";
import {PaginationModel} from "../models/Pagination";
import {Name} from "../models/Name";
import {getDeviceLicenceStatus} from "./utilities/StatusBuilder";
import {INTERNAL_SERVER_ERROR, NO_CONTENT} from "../constants/response-status-codes";
import {processServerError, getConstraintViolation} from "./utilities/ServiceError";
import {getProfileSubTypeFromBioMake} from "../utilities/BioUtils";
import { Translator } from "../contexts/translator";
import { TranslationKeys } from "../contexts/TranslationKeys";


const apiConnector = new APIConnector();

const buildBiometric = (biometric, translator: Translator): Biometric => {
    return {
        id: biometric['id'],
        name: biometric['ba-name'] ? biometric['ba-name'] : biometric['ba-address'],
        baAddress: biometric['ba-address'],
        ipAddress: biometric['ba-ip'],
        serialAddress: biometric['ba-serial'],
        site: biometric["device-improx"]?.['site']?.['site-name'] ?? '',
        siteId: biometric["device-improx"]?.["site"]?.['id'] ?? '',
        enabled: biometric['ba-enabled'],
        uploadEnabled: biometric['ba-upload-enabled'],
        port: biometric['ba-port'],
        device: biometric['device-improx']?.["dev-name"] ?? '',
        preset: biometric['bio-profile']?.['trn-name'] ?? '',
        presetId: biometric['bio-profile']?.['id'] ?? '',
        description: biometric['ba-description'] ?? '',
        version: biometric['ba-version'],
        biometricType: biometric['bio-type']?.['trn-name'] ?? '',
        biometricTypeId: biometric['bio-type']?.['id'] ?? '',
        biometricMake: biometric['bio-type']?.['bio-make'] ?? '',
        address: biometric['ba-address'],
        queue: 0,
        status: getDeviceLicenceStatus(biometric["unlocked"], biometric['ba-enabled'], biometric['ba-comms-status']),
        license: biometric["unlocked"],
        templatesReaderVsDB: undefined,
        musteringPoint: biometric["mustering-point"] === 1 || false,
        musteringPointLabel: biometric['mustering-point'] == 1 ? translator.byKey(TranslationKeys.yes) : translator.byKey(TranslationKeys.no),
    };
};

export const getInstalledBiometricDevices = async (translator: Translator, listParameters?: ListQuery): Promise<BiometricWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const siteFields = "fields[site]=siteName";
        const terminalFields = "fields[terminal]=devName,site";
        const bioProfileFields = "fields[profile]=trnName";
        const bioTypeFields = "fields[bioType]=trnName,bioMake";
        const bioAddressField = "fields[bioAddress]=bioProfile,baName,baAddress,baIp,baSerial,baEnabled,baUploadEnabled,baPort,baDescription,baVersion,musteringPoint,unlocked,baCommsStatus,deviceImprox,bioType";
        const include = "include=bioProfile,bioType,deviceImprox.site";
        const url = `bioAddress?${include}&${siteFields}&${terminalFields}&${bioProfileFields}&${bioTypeFields}&${bioAddressField}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const biometrics: Biometric[] = data.map((biometric) => buildBiometric(biometric, translator));
                return resolve({ rows: biometrics, pagination });
            })
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteBiometricDevice = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `bioAddress/${id}`;
        const floor = {
            data: {
                type: "bioAddress",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, floor);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();
            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricNamesWithPagination = async (listParameters?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = "bioAddress?fields[bioAddress]=baName,baAddress" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, apiBiometricNames: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const biometricName: Name[] = apiBiometricNames.map((biometric): Name => {
                    return { value: biometric['id'], label: biometric['ba-name'] ? biometric['ba-name'] : biometric['ba-address'], };
                });

                return resolve({ rows: biometricName, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricNamesWithCurrentSelectedDevice = async (listParameters?: ListQuery, currentSelectedDeviceId?: number): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = "bioAddress?include=site,bioProfile,deviceImprox.site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const biometricNames = data.filter((item) => {
                    if (item['device-improx'] == null || item['device-improx']['id'] == currentSelectedDeviceId) return item;
                });

                const biometricName: Name[] = biometricNames.map((biometric): Name => {
                    return {
                        value: biometric['id'],
                        label: biometric['ba-name'] ? biometric['ba-name'] : biometric['ba-address'],
                        optionalColumns: [{ key: 'ipAddress', value: biometric['ba-ip'] }]};
                });

                return resolve({ rows: biometricName, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricDeviceByAssignedDeviceImprox = async (assignedImproxId: string): Promise<Name> => {
    try {
        const url = `bioAddress?include=site,deviceImprox.site&filter=deviceImprox.id==${assignedImproxId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const rows: Name[] = data.map((biometric): Name => {
                    return {
                        value: biometric['id'],
                        label: biometric['ba-name'] ? biometric['ba-name'] : biometric['ba-address'],
                    }
                })

                return resolve(rows[0]);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getBiometricById = async (biometricId: string, translator: Translator): Promise<Biometric> => {
    try {
        const bioAddressFields = 'fields[bioAddress]=deviceImprox,bioType,bioProfile,baName,baAddress,baIp,baSerial,baEnabled,baUploadEnabled,baPort,baDescription,baVersion,musteringPoint,unlocked,baCommsStatus,site';
        const siteFields = 'fields[site]=siteName';
        const bioProfileFields = 'fields[profile]=trnName';
        const bioTypeFields = 'fields[bioType]=trnName,bioMake';
        const terminalFields = "fields[terminal]=devName,site";
        const url = `bioAddress/${biometricId}?include=site,bioProfile,deviceImprox.site,bioType&${bioAddressFields}&${terminalFields}&${siteFields}&${bioProfileFields}&${bioTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const device: Biometric = buildBiometric(data, translator);
                return resolve(device)
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeBiometric = async (biometric, createDevice = true) => {
    try {
        const newRecord = {
            data: {
                type: 'bioAddress',
                id: biometric.id,
                attributes: {
                    baName: biometric.name,
                    baIp: biometric.ipAddress,
                    baSerial: biometric.serialAddress,
                    baEnabled: biometric.enabled,
                    baDescription: biometric.description,
                    baAddress: biometric.address,
                    baVersion: biometric.version,
                    baUploadEnabled: biometric.uploadEnabled,
                    musteringPoint: !!biometric.musteringPoint,
                },
                relationships: {
                    bioType: {
                        data: {
                            type: "bioType",
                            id: biometric.biometricTypeId
                        },
                    },
                    bioProfile: {
                        data: {
                            type: "profile",
                            id: biometric.presetId,
                        }
                    }
                }
            }
        }

        if (biometric.siteId) {
            newRecord.data.relationships['site'] = createRelationship(biometric.siteId, 'site')
        }

        const url = `bioAddress${createDevice ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createDevice, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    reject({ status: response.status, message: genericError })
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createDevice) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) })
                    }

                    new Deserializer().deserialize(content, (error, newController) => {
                        if (error) {
                            return reject({ status: response.status, id: newController.id, success: false, message: genericError });
                        }

                        return resolve({ id: newController.id, success: true });
                    })

                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: biometric.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: biometric.id, success: false, message: getConstraintViolation(content.errors[0].detail) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: biometric.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const createRelationship = (id, typeName) => {
    return {
        data: {
            type: typeName,
            id: id
        }
    }
}

export const getBiometricDevices = async (translator: Translator, listParameters?: ListQuery, presetId?: string, exclude = false, pst_id?: string): Promise<BiometricWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const bioAddressFields = 'fields[bioAddress]=baName,baAddress,baIp,baSerial,site,baEnabled,baUploadEnabled,baPort,deviceImprox,bioProfile,baDescription,baVersion,bioType,unlocked,baCommsStatus,musteringPoint';
        const siteFields = 'fields[site]=siteName';
        const terminalFields = "fields[terminal]=devName,site";
        const deviceImproxFields = 'fields[deviceImprox]=devName';
        const bioProfileFields = 'fields[profile]=trnName';
        const bioTypeFields = 'fields[bioType]=trnName,bioMake';
        const include = "include=bioProfile,bioType,deviceImprox.site";
        const url = `bioAddress?${include}&${bioAddressFields}&${siteFields}&${deviceImproxFields}&${bioProfileFields}&${bioTypeFields}&${terminalFields}${createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        )}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                let devices = data.map((biometric) => buildBiometric(biometric, translator));

                if (presetId) {
                    devices = devices.filter((d) => (d.presetId === presetId) !== exclude)
                }

                if (pst_id) {
                    devices = devices.filter((d) => getProfileSubTypeFromBioMake(d.biometricMake ?? "")  === pst_id)
                }

                return resolve({ rows: devices, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignBiometricPresets = (presetId: string, ids: string[]) => {
    return Promise.all(ids.map(id => {
        return new Promise(async (resolve, reject) => {
            try {
                const newRecord = {
                    data: {
                        type: 'bioAddress',
                        id: id,
                        relationships: {
                            bioProfile: {
                                data: {
                                    type: "profile",
                                    id: presetId,
                                }
                            }
                        }
                    }
                }

                const url = `bioAddress/${newRecord.data.id}`;

                const response = await apiConnector.sendData(url, false, newRecord);

                if (response.status === NO_CONTENT) {
                    resolve({ id: id, success: true });
                } else {
                    const content = await response.json();
                    reject({ status: response.status, id: id, success: false, message: getConstraintViolation(content.errors[0].detail) });
                }
            } catch (error) {
                reject(processServerError(error));
            }
        });
    }));
}
